import type { Block, BlockLevel, BlockStatus } from "../../api/types";
import { UserRefChip } from "../../components/UserRefChip";
import { dueDateLabel, isOverdue } from "./dueDate";
import { pillarTint } from "./pillarColors";

export function statusLabel(status: BlockStatus): string {
  switch (status) {
    case "complete":
      return "Complete";
    case "in-progress":
      return "In Progress";
    case "pending-approval":
      return "Pending Approval";
    default:
      return "Assigned";
  }
}

// UI copy for the hierarchy: a pillar (company area, sponsored by an exec)
// holds Projects (top), a team lead owns a Block of a project (mid), and an IC
// owns a Sub-block (ic). Labels only — routes and wire values stay top/mid/ic.
export function levelLabel(level: BlockLevel): string {
  switch (level) {
    case "top":
      return "Project";
    case "mid":
      return "Block";
    default:
      return "Sub-block";
  }
}

// The section heading for a block's children: a Project holds Blocks, a Block
// holds Sub-blocks.
export function childrenLabel(level: BlockLevel): string {
  return level === "top" ? "Blocks" : "Sub-blocks";
}

export function OverdueChip({ dueDate, status }: { dueDate: string | null; status: BlockStatus }) {
  return isOverdue(dueDate, status) ? <span className="chip chip--overdue">Overdue</span> : null;
}

// A clickable block summary card, shared by the Dashboard and All Blocks views.
// The pillar tint drives the card's left-edge accent via a CSS custom property.
export function BlockCard({ block, onOpen }: { block: Block; onOpen: (id: string) => void }) {
  return (
    <button
      type="button"
      className="block-card block-card--tinted"
      style={{ "--pillar-tint": pillarTint(block.pillar_id, block.pillar) } as React.CSSProperties}
      onClick={() => onOpen(block.id)}
      aria-label={`${block.name} · ${block.size} blocks · ${statusLabel(block.status)}`}
    >
      <div className="block-card__main">
        <div className="block-card__left">
          <div className="block-card__name">{block.name}</div>
          <div className="block-card__tags">
            {block.impact !== null && block.significance !== null && (
              <span className="chip chip--impact">
                I{block.impact} S{block.significance}
              </span>
            )}
            <span className={`chip chip--status status-${block.status}`}>{statusLabel(block.status)}</span>
            <span className="chip">{dueDateLabel(block.due_date)}</span>
            <OverdueChip dueDate={block.due_date} status={block.status} />
          </div>
        </div>
        <div className="block-card__right">
          <div className="block-card__size">{block.size}</div>
          <div className="block-card__size-label">blocks</div>
        </div>
      </div>
      <div className="block-progress">
        <div
          className={`block-progress__fill${block.status === "complete" ? " is-complete" : ""}`}
          style={{ width: `${Math.min(block.progress, 100)}%` }}
        />
      </div>
      {(block.exec_owner !== null || block.assignee !== null) && (
        <div className="block-card__owners">
          {block.exec_owner !== null && <UserRefChip user={block.exec_owner} title="Owner" />}
          {block.assignee !== null && <UserRefChip user={block.assignee} title="Assignee" />}
        </div>
      )}
      {/* A Project's children are Blocks and a Block's are Sub-blocks; the
          capitalized level name keeps "2 Blocks" apart from the size unit. */}
      {block.child_count > 0 && (
        <div className="block-card__children">
          {block.child_count} {levelLabel(block.level === "top" ? "mid" : "ic")}
          {block.child_count > 1 ? "s" : ""} · {block.child_size_sum} blocks
        </div>
      )}
    </button>
  );
}
